/**
 * Feb 2026 · Purchase Receipt (GRN) — multi-line.
 *
 * Multi-line dialog: one supplier invoice can now book many raw materials
 * in a single save. Optional "Link to PO" picker auto-fills lines from
 * unreceived PO lines and the endpoint bumps qty_received on the parent
 * PO (Auto PO Match).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import RequiredVsActual from "@/components/RequiredVsActual";
import AllocationBadge from "@/components/AllocationBadge";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { RowActions } from "@/components/RowActions";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  PackageIcon, TruckIcon, WarningIcon, CheckCircleIcon,
  ArrowsClockwiseIcon, PlusIcon, MagnifyingGlassIcon, TrashIcon,
  ClipboardTextIcon, LinkIcon, EyeIcon, PencilSimpleIcon, PrinterIcon,
} from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";
import { fmtDateIST } from "@/lib/datetime";
import SearchablePicker from "@/components/SearchablePicker";

const inpCls = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const cellCls = "w-full h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]";
const lblCls = "text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block";

const emptyLine = () => ({ raw_material_id: "", quantity: "", godown_id: "", unit_cost: "", batch_no: "", shade_no: "", po_line_no: null, qty_pending: 0, remarks: "" });
const emptyHeader = () => ({
  supplier_id: "", supplier_name: "",
  invoice_no: "", invoice_date: "", txn_date: new Date().toISOString().slice(0, 10),
  purchase_order_id: "", remarks: "",
  lines: [emptyLine()],
});

const badgeCls = (q) => ({
  "Passed": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Awaiting Inspection": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Rejected": "bg-red-500/15 text-red-300 border-red-500/30",
  "Hold": "bg-sky-500/15 text-sky-300 border-sky-500/30",
}[q] || "bg-amber-500/15 text-amber-300 border-amber-500/30");

export default function PurchaseReceipt() {
  const [rms, setRms] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [openPOs, setOpenPOs] = useState([]);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyHeader());
  const [saving, setSaving] = useState(false);
  // Feb 2026 · GRN row actions — View / Edit / PDF. Delete uses a
  // native confirm to keep the modal count low.
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const rmById = useMemo(() => Object.fromEntries(rms.map(r => [r.id, r])), [rms]);
  // Feb 2026 · Default godown for a fresh GRN line is always the Raw
  // Material Store (code RM-STORE) — matches the shop-floor default so
  // buyers rarely need to touch the Godown picker. Falls back to legacy
  // RM-WH code for older installs that haven't run the code migration.
  const defaultGodownId = useMemo(() => {
    const match = godowns.find(g =>
      ["RM-STORE", "RM-WH"].includes((g.code || "").toUpperCase()) ||
      ["raw material store", "rm warehouse"].includes((g.name || "").toLowerCase())
    );
    return match ? match.id : "";
  }, [godowns]);
  const newLine = () => ({ ...emptyLine(), godown_id: defaultGodownId });

  const loadMasters = async () => {
    try {
      const [{ data: r }, { data: g }, { data: s }] = await Promise.all([
        api.get("/raw-materials/"),
        api.get("/godowns/"),
        api.get("/suppliers/").catch(() => ({ data: [] })),
      ]);
      setRms((r || []).filter(x => x.is_active !== false));
      setGodowns((g || []).filter(x => x.is_active !== false));
      setSuppliers((s || []).filter(x => x.is_active !== false));
    } catch (_e) { toast.error("Failed to load masters"); }
  };
  const loadReceipts = async () => {
    try {
      const { data } = await api.get("/inventory/purchase-receipts", { params: { limit: 200 } });
      setReceipts(data || []);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { loadMasters(); loadReceipts(); }, []);

  // When supplier is picked, fetch open POs for that supplier so the
  // "Link to PO" picker only shows relevant options.
  useEffect(() => {
    const load = async () => {
      if (!form.supplier_id) { setOpenPOs([]); return; }
      try {
        const { data } = await api.get(`/purchase-orders/open/for-supplier/${form.supplier_id}`);
        setOpenPOs(data || []);
      } catch (_e) { setOpenPOs([]); }
    };
    load();
  }, [form.supplier_id]);

  const openNew = () => { setForm({ ...emptyHeader(), lines: [newLine()] }); setDialogOpen(true); };
  const updLine = (idx, patch) => setForm(f => ({ ...f, lines: f.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, newLine()] }));
  // Backfill godown_id on existing lines the moment godowns finish
  // loading — covers the race where the dialog is opened before the
  // godowns API round-trip has completed.
  useEffect(() => {
    if (!defaultGodownId || !dialogOpen) return;
    setForm(f => {
      let touched = false;
      const lines = f.lines.map(l => {
        if (!l.godown_id) { touched = true; return { ...l, godown_id: defaultGodownId }; }
        return l;
      });
      return touched ? { ...f, lines } : f;
    });
  }, [defaultGodownId, dialogOpen]);
  const rmLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  // Feb 2026 · PO selection no longer auto-populates the line grid.
  // Buyer clicks "Add Item" and each picker only lists the RMs on the
  // linked PO with remaining balance so no external RM sneaks in.
  const linkPO = (poId) => {
    setForm(f => ({ ...f, purchase_order_id: poId, lines: [newLine()] }));
    if (poId) {
      const po = openPOs.find(p => p.id === poId);
      if (po) toast.success(`Linked ${po.po_no} · use "Add Item" to pick from its ${(po.lines || []).filter(l => Number(l.qty_pending || 0) > 0).length} pending line(s)`);
    }
  };

  // Pool of items that can still be received against the linked PO.
  // Feb 27, 2026 · Same PO line can now appear multiple times so operators
  // can split a single delivery into multiple batches (e.g. PO 1200 kg →
  // Batch-001 300 kg + Batch-002 280 kg). The picker keeps every PO line
  // visible; the cumulative-qty guard below enforces the balance.
  const selectedPo = useMemo(
    () => openPOs.find(p => p.id === form.purchase_order_id) || null,
    [openPOs, form.purchase_order_id],
  );
  const poItems = useMemo(() => {
    if (!selectedPo) return [];
    return (selectedPo.lines || []).filter(l => Number(l.qty_pending || 0) > 0);
  }, [selectedPo]);
  // Cumulative qty already entered per PO line across the current GRN —
  // used both to compute a per-line "sibling-consumed" tag and to hard-
  // stop the submit when the running total exceeds the PO balance.
  const cumulativeByPoLine = useMemo(() => {
    const map = {};
    for (const ln of form.lines) {
      if (!ln.po_line_no) continue;
      map[ln.po_line_no] = (map[ln.po_line_no] || 0) + (Number(ln.quantity) || 0);
    }
    return map;
  }, [form.lines]);

  // When an RM is picked, snap po_line_no / unit_cost / qty_pending from
  // the linked PO so the "Remaining" chip and Qty guard are accurate.
  const pickRm = (idx, rmId) => {
    if (!selectedPo) { updLine(idx, { raw_material_id: rmId, po_line_no: null, qty_pending: 0 }); return; }
    const poLine = (selectedPo.lines || []).find(l => l.raw_material_id === rmId);
    updLine(idx, {
      raw_material_id: rmId,
      po_line_no: poLine ? poLine.line_no : null,
      qty_pending: poLine ? Number(poLine.qty_pending || 0) : 0,
      unit_cost: poLine ? String(poLine.unit_cost || 0) : "",
    });
  };

  const canSubmit = form.invoice_no.trim() && form.txn_date &&
    (form.supplier_id || form.supplier_name.trim()) &&
    form.lines.length > 0 &&
    form.lines.every(l => l.raw_material_id && l.godown_id && Number(l.quantity) > 0);
    // Mar 1, 2026 · Over-receipt is NO LONGER a submit blocker — the
    // backend automatically splits the surplus into an EXCESS inventory
    // lot (is_excess=true, excess_status="Held for Return"). The old
    // cumulative guard defeated that rule and left buyers stuck when a
    // supplier over-delivered. The overage banner below tells the
    // operator exactly what will happen instead of silently blocking.

  // Mar 1, 2026 · Per-PO-line overage summary — surfaces the "will land
  // in Excess Inventory" callout so operators aren't surprised.
  const excessByPoLine = useMemo(() => {
    const out = {};
    if (!selectedPo) return out;
    for (const [lineNoStr, total] of Object.entries(cumulativeByPoLine)) {
      const poLine = (selectedPo.lines || []).find(pl => pl.line_no === Number(lineNoStr));
      if (!poLine) continue;
      const pending = Number(poLine.qty_pending || 0);
      const excess = Number(total) - pending;
      if (excess > 1e-4) {
        out[lineNoStr] = { pending, total: Number(total), excess: Math.round(excess * 1000) / 1000, rm_code: poLine.rm_code || "" };
      }
    }
    return out;
  }, [cumulativeByPoLine, selectedPo]);
  const totalExcess = Object.values(excessByPoLine).reduce((s, e) => s + e.excess, 0);

  const totalQty = form.lines.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
  const totalValue = form.lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error("Fill every required field on the header and at least one complete line"); return; }
    setSaving(true);
    try {
      const payload = {
        supplier_id: form.supplier_id || "",
        supplier_name: form.supplier_name.trim(),
        invoice_no: form.invoice_no.trim(),
        invoice_date: form.invoice_date || "",
        txn_date: form.txn_date,
        purchase_order_id: form.purchase_order_id || "",
        remarks: form.remarks.trim(),
        lines: form.lines.map(l => ({
          raw_material_id: l.raw_material_id,
          quantity: Number(l.quantity),
          godown_id: l.godown_id,
          unit_cost: Number(l.unit_cost || 0),
          batch_no: l.batch_no.trim(),
          shade_no: l.shade_no.trim(),
          po_line_no: l.po_line_no || null,
          remarks: l.remarks || "",
        })),
      };
      const { data } = await api.post("/inventory/purchase-receipt-multi", payload);
      let msg = `${data.lines_created} lot(s) booked · Awaiting Inspection`;
      if (data.po_updates?.closed_lines?.length) {
        msg += ` · PO line(s) closed: ${data.po_updates.closed_lines.join(", ")}`;
      }
      toast.success(msg);
      setDialogOpen(false);
      setForm(emptyHeader());
      loadReceipts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return receipts;
    return receipts.filter(r => [
      r.lot_no, r.rm_code, r.rm_name, r.godown_name, r.supplier_name,
      r.invoice_no, r.reference, r.quality_status, r.batch_no, r.shade_no,
    ].filter(Boolean).join(" ").toLowerCase().includes(needle));
  }, [receipts, q]);

  const exportCsv = () => {
    exportToCSV("purchase-receipts.csv", filtered.map(r => ({
      grn_no: r.grn_no || r.lot_no,
      date: (r.txn_date || "").slice(0, 10),
      code: r.rm_code, name: r.rm_name,
      qty: r.quantity, uom: r.uom, godown: r.godown_name,
      supplier: r.supplier_name, invoice: r.invoice_no || r.reference,
      invoice_date: r.invoice_date, batch: r.batch_no, shade: r.shade_no,
      unit_cost: r.unit_cost, status: r.quality_status,
      reversal_status: (r.void || (r.status || "").toLowerCase() === "voided") ? "Reversed" : "Active",
    })), [
      { key: "grn_no", label: "GRN No" }, { key: "date", label: "GRN Date" },
      { key: "invoice", label: "Invoice No" }, { key: "invoice_date", label: "Invoice Date" },
      { key: "supplier", label: "Supplier" },
      { key: "code", label: "RM Code" }, { key: "name", label: "Raw Material" },
      { key: "shade", label: "Shade No" }, { key: "batch", label: "Batch No" },
      { key: "qty", label: "Qty" }, { key: "uom", label: "UOM" },
      { key: "godown", label: "Godown" },
      { key: "unit_cost", label: "Unit Cost" }, { key: "status", label: "QC Status" },
      { key: "reversal_status", label: "Status" },
    ]);
  };

  const awaiting = receipts.filter(r => (r.quality_status || "Awaiting Inspection") === "Awaiting Inspection").length;

  const openView = async (row) => {
    try {
      const { data } = await api.get(`/inventory/purchase-receipts/${row.id}`);
      setViewRow(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  const openEdit = async (row) => {
    try {
      const { data } = await api.get(`/inventory/purchase-receipts/${row.id}`);
      setEditRow(data);
      setEditForm({
        invoice_no: data.invoice_no || data.reference || "",
        invoice_date: data.invoice_date || "",
        batch_no: data.batch_no || "",
        shade_no: data.shade_no || "",
        remarks: data.remarks || "",
        quantity: data.qty_received ?? data.quantity ?? "",
        unit_cost: data.unit_cost ?? "",
        godown_id: data.godown_id || "",
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  const saveEdit = async () => {
    if (!editRow) return;
    const payload = {};
    for (const k of ["invoice_no", "invoice_date", "batch_no", "shade_no", "remarks"]) {
      payload[k] = editForm[k] ?? "";
    }
    const restrictedEditable = (editRow.quality_status || "Awaiting Inspection") !== "Passed" && Number(editRow.qty_used || 0) === 0;
    if (restrictedEditable) {
      payload.quantity = Number(editForm.quantity);
      payload.unit_cost = Number(editForm.unit_cost);
      payload.godown_id = editForm.godown_id;
    }
    setEditSaving(true);
    try {
      await api.patch(`/inventory/purchase-receipts/${editRow.id}`, payload);
      toast.success(`GRN ${editRow.lot_no || ""} updated`);
      setEditRow(null); loadReceipts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setEditSaving(false); }
  };
  const deleteRow = async (row) => {
    const label = row.lot_no || row.rm_code || "this receipt";
    if (!window.confirm(`Delete GRN ${label}? This will reverse the lot, roll back any PO qty_received, and (if already Passed) decrement current stock.`)) return;
    try {
      await api.delete(`/inventory/purchase-receipts/${row.id}`);
      toast.success(`GRN ${label} deleted · lot reversed`);
      loadReceipts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const printRow = async (row) => {
    try {
      const resp = await api.get(`/inventory/purchase-receipts/${row.id}/pdf`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) { toast.error(e?.response?.data?.detail || "PDF failed"); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="purchase-receipt-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Inventory · Inward</div>
          <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-3">
            <TruckIcon size={26} weight="bold" className="text-[var(--accent)]" />
            Purchase Receipt (GRN)
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            One invoice can now book many RM lines in a single save. Optional PO link auto-fills lines and closes matched PO lines · booked lots land as
            <span className="text-amber-300 font-semibold ml-1">Awaiting Inspection</span> · cleared on the
            <Link to="/qc-approval" className="text-[var(--accent)] hover:underline ml-1">QC Approval</Link> queue.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/purchase-orders" data-testid="grn-view-pos"
            className="h-10 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2">
            <ClipboardTextIcon size={13} weight="bold" />Purchase Orders
          </Link>
          <button data-testid="new-grn-btn" onClick={openNew}
            className="h-10 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" />New GRN
          </button>
          <button data-testid="grn-export" onClick={exportCsv} disabled={filtered.length === 0}
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm disabled:opacity-40">
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="tactical-card p-4"><div className="overline mb-1">Total Receipts</div><div className="font-mono text-3xl font-semibold text-white">{receipts.length}</div></div>
        <div className="tactical-card p-4 border-l-2 border-l-amber-500"><div className="overline text-amber-300 mb-1">Awaiting Inspection</div><div className="font-mono text-3xl font-semibold text-amber-400" data-testid="grn-awaiting-count">{awaiting}</div></div>
        <div className="tactical-card p-4"><div className="overline mb-1">Passed</div><div className="font-mono text-3xl font-semibold text-emerald-400">{receipts.filter(r => r.quality_status === "Passed").length}</div></div>
        <div className="tactical-card p-4"><div className="overline mb-1">Filtered</div><div className="font-mono text-3xl font-semibold text-zinc-300">{filtered.length}</div></div>
      </div>

      <div className="tactical-card p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlassIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="grn-search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search lot / RM / supplier / invoice / batch / shade…"
              className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
          </div>
          <button data-testid="grn-refresh" onClick={loadReceipts}
            className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2">
            <ArrowsClockwiseIcon size={13} weight="bold" />Refresh
          </button>
          <span className="ml-auto text-xs text-zinc-500">{filtered.length} of {receipts.length}</span>
        </div>
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">GRN No</th>
                <th className="px-3 py-2 text-left">GRN Date</th>
                <th className="px-3 py-2 text-left">Invoice No</th>
                <th className="px-3 py-2 text-left">Invoice Date</th>
                <th className="px-3 py-2 text-left">Supplier</th>
                {/* Mar 03, 2026 · PO No column — surfaces the parent PO
                    each GRN was booked against so operators can trace
                    linkage without opening the row. */}
                <th className="px-3 py-2 text-left">PO No</th>
                {/* P17-UAT-GRN-WO-LINEAGE-01 · WO Ref (production authority) and
                    SO/ESO Ref (commercial parent) shown as SEPARATE columns. */}
                <th className="px-3 py-2 text-left">WO Ref</th>
                <th className="px-3 py-2 text-left">SO / ESO Ref</th>
                <th className="px-3 py-2 text-left">Raw Material</th>
                <th className="px-3 py-2 text-left">Shade No</th>
                <th className="px-3 py-2 text-left">Batch No</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-left">Godown</th>
                <th className="px-3 py-2 text-right">Unit Cost</th>
                <th className="px-3 py-2 text-left">QC Status</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-center w-32">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={17} className="text-center py-8 text-zinc-500 text-xs">
                  <PackageIcon size={20} weight="bold" className="inline mr-2" />
                  {receipts.length === 0 ? "No receipts booked yet — click New GRN to add the first one." : "No receipts match the current search."}
                </td></tr>
              )}
              {filtered.map((t, i) => {
                const isReversed = t.void || (t.status || "").toLowerCase() === "voided";
                return (
                <tr key={t.id} data-testid={`grn-row-${i}`} className={`border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30 ${isReversed ? "opacity-60" : ""}`}>
                  <td className="px-3 py-2 text-xs text-right"><TwoLineDocNo value={t.grn_no || t.lot_no || "—"} className="text-[var(--accent)]" /></td>
                  <td className="px-3 py-2 text-xs font-mono text-zinc-400 whitespace-nowrap">{fmtDateIST(t.txn_date)}</td>
                  <td className="px-3 py-2 text-xs font-mono text-zinc-300">{t.invoice_no || t.reference || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono text-zinc-400">{fmtDateIST(t.invoice_date)}</td>
                  <td className="px-3 py-2 text-xs">{t.supplier_name || "—"}</td>
                  <td className="px-3 py-2 text-xs" data-testid={`grn-po-${i}`}>
                    {t.po_no ? (
                      <div className="font-mono text-emerald-300">{t.po_no}{t.po_line_no ? <span className="text-zinc-500"> · #{t.po_line_no}</span> : null}</div>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono" data-testid={`grn-wo-${i}`}>
                    {t.wo_ref ? <span className="text-amber-300">{t.wo_ref}</span> : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono" data-testid={`grn-soeso-${i}`}>
                    {t.so_eso_ref ? <span className="text-sky-300">{t.so_eso_ref}</span> : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {(t.required_rm_id && t.required_rm_id !== t.raw_material_id) ? (
                      <RequiredVsActual
                        requiredCode={t.required_rm_code || "—"}
                        actualCode={t.rm_code || "—"}
                        allocationType={t.allocation_type || "APPROVED_ALTERNATE"}
                        alternateApprovalNo={t.alternate_approval_no}
                      />
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        <span className="font-mono text-[var(--accent)]">{t.rm_code || "—"}</span>
                        <div className="flex items-center gap-1">
                          <AllocationBadge
                            type={t.legacy_h1_zeta_missing_genealogy ? "LEGACY" : (t.allocation_type || "EXACT")}
                            size="sm"
                          />
                          <span className="text-[10px] text-zinc-500">{t.rm_name}</span>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-zinc-300">{t.shade_no || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono text-zinc-300">{t.batch_no || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-sm">{Number(t.quantity).toLocaleString("en-IN", { maximumFractionDigits: 3 })} <span className="text-[10px] text-zinc-500">{t.uom}</span></td>
                  <td className="px-3 py-2 text-xs">{t.godown_name || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-xs text-zinc-400">{t.unit_cost ? `₹${Number(t.unit_cost).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${badgeCls(t.quality_status)}`}>
                        {t.quality_status || "Awaiting Inspection"}
                      </span>
                      {/* Mar 03, 2026 · Auto-Reservation ripple indicator.
                          When a QC-pass on this lot triggered an ACTIVE
                          `stock_reservations` row keyed on the parent PO's
                          Sales/Work Order, surface it here so ops know the
                          ripple happened and Free-Stock got debited. */}
                      {t.auto_reservation_id && (
                        <span
                          data-testid={`grn-autores-${i}`}
                          title={`Auto-reserved ${Number(t.auto_reservation_qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })} ${t.uom || ""} against ${t.auto_reservation_type || ""} ${t.auto_reservation_ref || ""}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-sm border border-purple-500/50 bg-purple-500/10 text-purple-300 text-[10px] font-mono uppercase tracking-widest"
                        >
                          ● Auto-Res
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2" data-testid={`grn-reversal-status-${i}`}>
                    {isReversed ? (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest"
                        title={t.reversal_reason ? `Reason: ${t.reversal_reason}` : "Reversed via Live Transaction Reversal"}
                      >Reversed</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest">Active</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <RowActions
                      testid={`grn-actions-${i}`}
                      items={[
                        { label: "View receipt", testid: `grn-view-${i}`, onClick: () => openView(t) },
                        { label: "Edit receipt", testid: `grn-edit-${i}`, onClick: () => openEdit(t) },
                        { label: "Print / PDF", testid: `grn-print-${i}`, onClick: () => printRow(t) },
                        {
                          label: "Reverse", tone: "warn", testid: `grn-reverse-${i}`,
                          disabled: t.void || (t.status || "").toLowerCase() === "voided",
                          onClick: async () => {
                            if (t.void || (t.status || "").toLowerCase() === "voided") { toast.info("Receipt already reversed"); return; }
                            const reason = window.prompt(
                              `Reverse Purchase Receipt ${t.grn_no || ""}?\n\nPosts an equal-and-opposite stock movement + Kardex entry, voids the lot, and flags the GRN Voided (audit trail preserved).\n\nReason (min 4 chars):`, "",
                            );
                            if (reason === null) return;
                            if ((reason || "").trim().length < 4) { toast.error("Reversal reason must be at least 4 characters"); return; }
                            try {
                              const { data } = await api.post(`/inventory/purchase-receipts/${t.id}/reverse-entry`, { reason: reason.trim() });
                              toast.success(`${data.grn_no || t.grn_no} · ${data.reversal_legs} leg(s) reversed`);
                              loadReceipts();
                            } catch (e) { toast.error(e?.response?.data?.detail || "Reversal failed"); }
                          },
                        },
                      ]}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* View GRN Dialog */}
      <Dialog open={!!viewRow} onOpenChange={(o) => !o && setViewRow(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl" data-testid="grn-view-dialog" description="GRN action — draft, edit or preview a Purchase Receipt.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <EyeIcon size={16} weight="bold" className="text-[var(--accent)]" />
              GRN · {viewRow?.lot_no || "—"}
              <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${badgeCls(viewRow?.quality_status)}`}>
                {viewRow?.quality_status || "Awaiting Inspection"}
              </span>
            </DialogTitle>
          </DialogHeader>
          {viewRow && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-xs pt-2">
              <div><div className={lblCls}>Receipt Date</div><div className="font-mono text-white">{fmtDateIST(viewRow.txn_date)}</div></div>
              <div><div className={lblCls}>Godown</div><div className="text-white">{viewRow.godown_name || "—"}</div></div>
              <div><div className={lblCls}>Supplier</div><div className="text-white">{viewRow.supplier_name || "—"}</div></div>
              <div><div className={lblCls}>Invoice</div><div className="font-mono text-zinc-300">{viewRow.invoice_no || viewRow.reference || "—"} <span className="text-zinc-500 ml-2">{fmtDateIST(viewRow.invoice_date)}</span></div></div>
              <div className="col-span-2"><div className={lblCls}>Raw Material</div><div className="font-mono text-[var(--accent)]">{viewRow.rm_code}</div><div className="text-[10px] text-zinc-500">{viewRow.rm_name}</div></div>
              <div><div className={lblCls}>Qty Received</div><div className="font-mono text-white">{Number(viewRow.qty_received || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })} <span className="text-zinc-500">{viewRow.uom}</span></div></div>
              <div><div className={lblCls}>Qty Consumed</div><div className="font-mono text-amber-300">{Number(viewRow.qty_used || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</div></div>
              <div><div className={lblCls}>Rate</div><div className="font-mono text-zinc-300">₹{Number(viewRow.unit_cost || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
              <div><div className={lblCls}>Value</div><div className="font-mono text-zinc-300">₹{(Number(viewRow.qty_received || 0) * Number(viewRow.unit_cost || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
              <div><div className={lblCls}>Batch No</div><div className="font-mono text-zinc-300">{viewRow.batch_no || "—"}</div></div>
              <div><div className={lblCls}>Shade No</div><div className="font-mono text-zinc-300">{viewRow.shade_no || "—"}</div></div>
              {viewRow.po_no && (
                <div className="col-span-2"><div className={lblCls}>Linked PO</div><div className="font-mono text-emerald-300">{viewRow.po_no} · line #{viewRow.po_line_no || "—"}</div></div>
              )}
              {/* P17-UAT-GRN-WO-LINEAGE-01 · WO Ref + SO/ESO Ref + MRP/PRQ shown separately. */}
              <div><div className={lblCls}>WO Ref</div><div className="font-mono text-amber-300" data-testid="grn-view-wo">{viewRow.wo_ref || "—"}</div></div>
              <div><div className={lblCls}>SO / ESO Ref</div><div className="font-mono text-sky-300" data-testid="grn-view-soeso">{viewRow.so_eso_ref || "—"}</div></div>
              <div><div className={lblCls}>MRP Ref</div><div className="font-mono text-zinc-300">{viewRow.mrp_ref || "—"}</div></div>
              <div><div className={lblCls}>PRQ Ref</div><div className="font-mono text-zinc-300">{viewRow.prq_ref || "—"}</div></div>
              {viewRow.remarks && (
                <div className="col-span-2"><div className={lblCls}>Remarks</div><div className="text-zinc-300">{viewRow.remarks}</div></div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setViewRow(null)} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">Close</button>
            <button onClick={() => { const r = viewRow; setViewRow(null); printRow(r); }} className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-sm flex items-center gap-2">
              <PrinterIcon size={12} weight="bold" />Print PDF
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit GRN Dialog */}
      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl" data-testid="grn-edit-dialog" description="GRN action — draft, edit or preview a Purchase Receipt.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <PencilSimpleIcon size={16} weight="bold" className="text-amber-400" />
              Edit GRN · {editRow?.lot_no || "—"}
            </DialogTitle>
          </DialogHeader>
          {editRow && (() => {
            const locked = (editRow.quality_status || "Awaiting Inspection") === "Passed" || Number(editRow.qty_used || 0) > 0;
            return (
              <div className="space-y-3 pt-2">
                {locked && (
                  <div className="text-[11px] flex items-start gap-2 bg-amber-500/10 border border-amber-500/40 text-amber-300 rounded-sm px-3 py-2">
                    <WarningIcon size={14} weight="bold" className="mt-0.5" />
                    <div>This lot is <b>{editRow.quality_status}</b>{Number(editRow.qty_used || 0) > 0 ? ` with ${editRow.qty_used} ${editRow.uom} already consumed` : ""} — only metadata (Invoice / Batch / Shade / Remarks) is editable. Raise a Stock Adjustment to change qty or rate.</div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={lblCls}>Invoice No</label>
                    <input value={editForm.invoice_no} onChange={e => setEditForm(f => ({ ...f, invoice_no: e.target.value }))} className={inpCls} data-testid="grn-edit-invoice" />
                  </div>
                  <div>
                    <label className={lblCls}>Invoice Date</label>
                    <input type="date" value={editForm.invoice_date} onChange={e => setEditForm(f => ({ ...f, invoice_date: e.target.value }))} className={inpCls} data-testid="grn-edit-invoice-date" />
                  </div>
                  <div>
                    <label className={lblCls}>Batch No</label>
                    <input value={editForm.batch_no} onChange={e => setEditForm(f => ({ ...f, batch_no: e.target.value }))} className={inpCls} data-testid="grn-edit-batch" />
                  </div>
                  <div>
                    <label className={lblCls}>Shade No</label>
                    <input value={editForm.shade_no} onChange={e => setEditForm(f => ({ ...f, shade_no: e.target.value }))} className={inpCls} data-testid="grn-edit-shade" />
                  </div>
                  <div>
                    <label className={lblCls}>Qty Received {locked && <span className="text-zinc-600">(locked)</span>}</label>
                    <input type="number" step="0.001" min="0" value={editForm.quantity} disabled={locked} onChange={e => setEditForm(f => ({ ...f, quantity: e.target.value }))} className={`${inpCls} ${locked ? "opacity-50" : ""}`} data-testid="grn-edit-qty" />
                  </div>
                  <div>
                    <label className={lblCls}>Unit Cost {locked && <span className="text-zinc-600">(locked)</span>}</label>
                    <input type="number" step="0.01" min="0" value={editForm.unit_cost} disabled={locked} onChange={e => setEditForm(f => ({ ...f, unit_cost: e.target.value }))} className={`${inpCls} ${locked ? "opacity-50" : ""}`} data-testid="grn-edit-cost" />
                  </div>
                  <div className="col-span-2">
                    <label className={lblCls}>Godown {locked && <span className="text-zinc-600">(locked)</span>}</label>
                    <select value={editForm.godown_id} disabled={locked} onChange={e => setEditForm(f => ({ ...f, godown_id: e.target.value }))} className={`${inpCls} ${locked ? "opacity-50" : ""}`} data-testid="grn-edit-godown">
                      {godowns.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className={lblCls}>Remarks</label>
                    <input value={editForm.remarks} onChange={e => setEditForm(f => ({ ...f, remarks: e.target.value }))} className={inpCls} data-testid="grn-edit-remarks" />
                  </div>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <button onClick={() => setEditRow(null)} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm" disabled={editSaving}>Cancel</button>
            <button onClick={saveEdit} disabled={editSaving} data-testid="grn-edit-save" className="h-9 px-5 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40">
              <CheckCircleIcon size={12} weight="bold" />{editSaving ? "Saving…" : "Save Changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New GRN Dialog · multi-line */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="bg-[var(--bg-1)] border-[var(--border)] w-screen h-screen max-w-none rounded-none p-6 sm:p-8 overflow-y-auto flex flex-col"
          data-testid="grn-dialog"
          description="GRN action — draft, edit or preview a Purchase Receipt."
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
          onEscapeKeyDown={e => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <TruckIcon size={20} weight="bold" className="text-[var(--accent)]" />
              New GRN — Purchase Receipt Entry
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest bg-amber-500/15 text-amber-300 border border-amber-500/30">
                <WarningIcon size={10} weight="fill" />Awaiting Inspection
              </span>
              {form.purchase_order_id && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                  <LinkIcon size={10} weight="bold" />
                  {openPOs.find(p => p.id === form.purchase_order_id)?.po_no || "Linked"}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <form id="grn-form" onSubmit={submit} className="space-y-4 mt-2">
            {/* Header */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className={lblCls}>Supplier *</label>
                <SearchablePicker
                  testid="grn-supplier"
                  value={form.supplier_id}
                  onChange={(sid) => {
                    const sup = suppliers.find(s => s.id === sid);
                    setForm(f => ({ ...f, supplier_id: sid, supplier_name: sup ? sup.name : f.supplier_name, purchase_order_id: "" }));
                  }}
                  placeholder="— pick from Supplier Master —"
                  headerHint="Search by supplier code or name"
                  options={suppliers.map(s => ({ value: s.id, code: s.code, name: s.name }))}
                  clearable
                />
                <input data-testid="grn-supplier-name" type="text" value={form.supplier_name}
                  onChange={e => setForm(f => ({ ...f, supplier_name: e.target.value }))}
                  placeholder="…or type Supplier Name (one-off)"
                  className={`${inpCls} mt-2`} />
              </div>

              <div>
                <label className={lblCls}>Invoice / Challan No *</label>
                <input data-testid="grn-invoice" type="text" value={form.invoice_no}
                  onChange={e => setForm(f => ({ ...f, invoice_no: e.target.value }))}
                  className={inpCls} placeholder="INV-2026-01234" required />
              </div>

              <div>
                <label className={lblCls}>Invoice Date</label>
                <input data-testid="grn-invoice-date" type="date" value={form.invoice_date}
                  onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))}
                  className={inpCls} />
              </div>

              <div>
                <label className={lblCls}>Receipt Date *</label>
                <input data-testid="grn-date" type="date" value={form.txn_date}
                  onChange={e => setForm(f => ({ ...f, txn_date: e.target.value }))}
                  className={inpCls} required />
              </div>

              <div className="md:col-span-2">
                <label className={lblCls}>Link to Purchase Order <span className="text-[9px] text-zinc-600">(auto-fills lines · Auto PO Match)</span></label>
                <select data-testid="grn-po-link" value={form.purchase_order_id}
                  onChange={e => linkPO(e.target.value)} className={inpCls}
                  disabled={!form.supplier_id}>
                  <option value="">
                    {form.supplier_id
                      ? (openPOs.length === 0 ? "No open POs for this supplier" : "— optional · pick a PO to auto-fill lines —")
                      : "Select supplier first…"}
                  </option>
                  {openPOs.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.po_no} · {(p.po_date || "").slice(0, 10)} · {(p.lines || []).filter(l => Number(l.qty_pending || 0) > 0).length} pending line(s)
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className={lblCls}>Remarks</label>
                <input data-testid="grn-remarks" type="text" value={form.remarks}
                  onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                  className={inpCls} placeholder="Optional notes (batch, gate pass, transporter…)" />
              </div>
            </div>

            {/* Line items */}
            <div className="border-t border-[var(--border)] pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="overline">Line Items · {form.lines.length}
                  {selectedPo && (
                    <span className="ml-2 text-[10px] normal-case tracking-normal text-zinc-500">
                      · picker restricted to <span className="text-emerald-300">{selectedPo.po_no}</span> ({poItems.length} pending item{poItems.length === 1 ? "" : "s"} · multiple batches per RM allowed)
                    </span>
                  )}
                </div>
                <button type="button" data-testid="grn-add-line" onClick={addLine}
                  disabled={selectedPo && poItems.length <= 0}
                  title={selectedPo && poItems.length <= 0 ? "This PO has no pending items" : "Add another line item — same RM can be added multiple times for split batches"}
                  className="h-8 px-3 border border-[var(--accent)] bg-[var(--accent)]/10 hover:bg-[var(--accent)]/20 text-[var(--accent)] text-xs rounded-sm flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
                  <PlusIcon size={11} weight="bold" />Add Item
                </button>
              </div>
              <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
                    <tr>
                      <th className="px-2 py-1.5 text-left w-8">#</th>
                      <th className="px-2 py-1.5 text-left min-w-[200px]">Raw Material *</th>
                      <th className="px-2 py-1.5 text-right w-36">Qty *</th>
                      <th className="px-2 py-1.5 text-left w-40">Godown *</th>
                      <th className="px-2 py-1.5 text-right w-28">Unit Cost</th>
                      <th className="px-2 py-1.5 text-left w-32">Batch No</th>
                      <th className="px-2 py-1.5 text-left w-32">Shade No</th>
                      <th className="px-2 py-1.5 text-left w-20">PO Line</th>
                      <th className="px-2 py-1.5 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((l, i) => {
                      const rm = rmById[l.raw_material_id];
                      // Feb 27, 2026 · Picker keeps every pending PO line
                      // visible — duplicates are allowed so multiple
                      // batches of the same RM can be split into separate
                      // GRN lines. The cumulative-qty guard prevents
                      // over-receipt against the PO balance.
                      const rmOptions = selectedPo
                        ? poItems.map(pl => ({ id: pl.raw_material_id, code: pl.rm_code || rmById[pl.raw_material_id]?.code || "", name: pl.rm_name || rmById[pl.raw_material_id]?.name || "" }))
                        : rms.map(r => ({ id: r.id, code: r.code, name: r.name }));
                      const remaining = Number(l.qty_pending || 0);
                      // Feb 27, 2026 · Sibling lines that hit the same PO
                      // line already consume part of the balance — show
                      // the split so the operator sees the true headroom.
                      const siblingConsumed = l.po_line_no
                        ? (cumulativeByPoLine[l.po_line_no] || 0) - (Number(l.quantity) || 0)
                        : 0;
                      const headroom = Math.max(0, remaining - siblingConsumed);
                      const overQty = Boolean(l.po_line_no) && (Number(l.quantity) || 0) > headroom + 1e-6;
                      return (
                        <tr key={i} className="border-t border-[var(--border)]" data-testid={`grn-line-${i}`}>
                          <td className="px-2 py-1.5 text-xs font-mono text-zinc-500">{i + 1}</td>
                          <td className="px-2 py-1.5">
                            <SearchablePicker
                              testid={`grn-line-${i}-rm`}
                              value={l.raw_material_id || ""}
                              onChange={(v) => pickRm(i, v)}
                              placeholder={selectedPo ? "Select RM from PO…" : "Select RM…"}
                              headerHint="Search by RM code (RM-001) or name (Yarn 70D · Nylon)"
                              options={rmOptions.map(r => ({ value: r.id, code: r.code, name: r.name }))}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            {/* Feb 2026 · Qty input widened & re-flowed. The
                                previous single-row layout (`input + REM button
                                inline`) collapsed the number field to ~40px,
                                clipping any qty > 3 digits. Now the input
                                takes the full cell width and the "Rem" quick-
                                fill button sits below on its own row alongside
                                the remaining-balance chip. */}
                            <input data-testid={`grn-line-${i}-qty`} type="number" step="0.001" min="0"
                              value={l.quantity} onChange={e => updLine(i, { quantity: e.target.value })}
                              className={`${cellCls} text-right font-mono tabular-nums ${overQty ? "border-amber-500/60 text-amber-300" : ""}`}
                              title={overQty ? `Over-receipt by ${(Number(l.quantity) - headroom).toLocaleString("en-IN", { maximumFractionDigits: 3 })} ${rm?.uom || ""} — will auto-post to Excess Inventory (Held for Return)` : ""}
                              required />
                            {l.po_line_no && remaining > 0 && (
                              <div className="flex items-center justify-end gap-1 mt-0.5">
                                <span className={`text-[9px] font-mono ${overQty ? "text-amber-400" : "text-zinc-500"}`}
                                  data-testid={`grn-line-${i}-remaining`}
                                  title={overQty
                                    ? `Over-receipt — surplus auto-posts to Excess Inventory (Held for Return). PO balance ${remaining.toLocaleString("en-IN")} ${rm?.uom || ""}.`
                                    : siblingConsumed > 0
                                    ? `PO balance ${remaining.toLocaleString("en-IN")} − sibling batches on this GRN ${siblingConsumed.toLocaleString("en-IN")} = ${headroom.toLocaleString("en-IN")} available on this line`
                                    : "PO Remaining Balance — surplus lands in Excess Inventory"}>
                                  Rem: {headroom.toLocaleString("en-IN", { maximumFractionDigits: 3 })}{siblingConsumed > 0 ? ` / ${remaining.toLocaleString("en-IN")}` : ""} {rm?.uom || ""}
                                </span>
                                <button
                                  type="button"
                                  data-testid={`grn-line-${i}-fill-rem`}
                                  onClick={() => updLine(i, { quantity: String(remaining) })}
                                  title="Fill Qty with the PO's remaining balance"
                                  className="h-5 px-1.5 rounded-sm border border-[var(--accent)]/50 bg-[var(--accent)]/10 hover:bg-[var(--accent)]/20 text-[var(--accent)] text-[9px] font-mono uppercase tracking-widest"
                                >
                                  Fill
                                </button>
                              </div>
                            )}
                            {!l.po_line_no && rm && <div className="text-[9px] text-zinc-500 text-right mt-0.5">{rm.uom}</div>}
                          </td>
                          <td className="px-2 py-1.5">
                            <select data-testid={`grn-line-${i}-godown`} value={l.godown_id}
                              onChange={e => updLine(i, { godown_id: e.target.value })}
                              className={cellCls} required>
                              <option value="">Select…</option>
                              {godowns.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <input data-testid={`grn-line-${i}-cost`} type="number" step="0.01" min="0"
                              value={l.unit_cost} onChange={e => updLine(i, { unit_cost: e.target.value })}
                              className={`${cellCls} text-right font-mono`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <input data-testid={`grn-line-${i}-batch`} type="text" value={l.batch_no}
                              onChange={e => updLine(i, { batch_no: e.target.value })}
                              className={cellCls} placeholder="B-…" />
                          </td>
                          <td className="px-2 py-1.5">
                            <input data-testid={`grn-line-${i}-shade`} type="text" value={l.shade_no}
                              onChange={e => updLine(i, { shade_no: e.target.value })}
                              className={cellCls} placeholder="SH-…" />
                          </td>
                          <td className="px-2 py-1.5 text-xs font-mono text-center">
                            {l.po_line_no ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 text-[10px]">
                                <LinkIcon size={9} weight="bold" />#{l.po_line_no}
                              </span>
                            ) : <span className="text-zinc-600">—</span>}
                          </td>
                          <td className="px-2 py-1.5">
                            {form.lines.length > 1 && (
                              <button type="button" data-testid={`grn-line-${i}-del`} onClick={() => rmLine(i)}
                                className="p-1 text-zinc-500 hover:text-red-400">
                                <TrashIcon size={12} weight="bold" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-[var(--border)] bg-[var(--bg-2)]/30">
                      <td colSpan={2} className="px-2 py-2 text-right text-xs uppercase tracking-widest text-zinc-500">Totals</td>
                      <td className="px-2 py-2 text-right font-mono text-sm text-white font-semibold" data-testid="grn-total-qty">{totalQty.toLocaleString("en-IN", { maximumFractionDigits: 3 })}</td>
                      <td colSpan={2} className="px-2 py-2 text-right text-xs uppercase tracking-widest text-zinc-500">Value ₹</td>
                      <td colSpan={3} className="px-2 py-2 text-right font-mono text-sm text-white font-semibold" data-testid="grn-total-value">₹{Number(totalValue).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-xs text-zinc-500 flex items-center gap-1.5 pt-1">
              <WarningIcon size={12} weight="fill" className="text-amber-400" />
              Every booked lot is <b className="text-amber-300 mx-1">held in Awaiting Inspection</b> until QC clears it.
            </div>

            {/* Mar 1, 2026 · Excess Receipt callout — appears when any PO line
                is over-received. Reassures the operator that the surplus is
                being captured, not blocked. */}
            {totalExcess > 1e-4 && (
              <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 text-xs" data-testid="grn-excess-banner">
                <div className="flex items-center gap-2 font-semibold text-amber-300 mb-1">
                  <WarningIcon size={13} weight="fill" />
                  Over-receipt detected · {totalExcess.toLocaleString("en-IN", { maximumFractionDigits: 3 })} unit(s) will auto-post to Excess Inventory
                </div>
                <div className="text-amber-200/80 leading-relaxed">
                  The surplus lands in a separate lot flagged <span className="font-mono">is_excess=true · Held for Return</span>.
                  Procurement can approve for consumption or return-to-supplier from <b>Purchase &amp; Inventory → Excess Inventory</b>.
                </div>
                <ul className="mt-1.5 space-y-0.5 text-[11px] text-amber-100/90 font-mono">
                  {Object.entries(excessByPoLine).map(([ln, e]) => (
                    <li key={ln}>PO Line #{ln} <span className="text-amber-300">{e.rm_code}</span> · booked {e.total.toLocaleString("en-IN")} vs pending {e.pending.toLocaleString("en-IN")} → <b>excess {e.excess.toLocaleString("en-IN")}</b></li>
                  ))}
                </ul>
              </div>
            )}
          </form>

          <DialogFooter className="gap-2">
            <button type="button" data-testid="grn-cancel" onClick={() => setDialogOpen(false)}
              className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">Cancel</button>
            <button type="submit" form="grn-form" data-testid="grn-submit"
              disabled={!canSubmit || saving}
              className="h-10 px-5 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40">
              <CheckCircleIcon size={14} weight="bold" />
              {saving ? "Booking…" : `Book ${form.lines.length} Receipt${form.lines.length > 1 ? "s" : ""}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
